import { useContext, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import axios from 'axios'
import Bugsnag from '@bugsnag/js'
import Cropper from 'react-easy-crop'

import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Typography,
} from '@mui/material'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'

import { authContext } from 'contexts/AuthContext'
import resizeFile from 'services/helper/fileResizer'
import { presignURL, storeProfilePhoto } from 'services/api/presign-url'
import { getFileExtension, generateFileName } from 'services/helper/preSignHelper'
import AdxInfoModal from '../modal/AdxInfoModal'
import defaultAvatar from 'assets/images/default_avatar.png'

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180

const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation)

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  }
}

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Canvas context is not supported.')
  }

  const rotRad = getRadianAngle(rotation)
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  )

  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
  ctx.rotate(rotRad)
  ctx.translate(-image.width / 2, -image.height / 2)
  ctx.drawImage(image, 0, 0)

  const croppedCanvas = document.createElement('canvas')
  const croppedCtx = croppedCanvas.getContext('2d')

  if (!croppedCtx) {
    throw new Error('Cropped canvas context is not supported.')
  }

  croppedCanvas.width = pixelCrop.width
  croppedCanvas.height = pixelCrop.height

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  )

  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to generate cropped image blob.'))
          return
        }

        const croppedFile = new File([blob], 'profile-photo.jpg', {
          type: 'image/jpeg',
        })

        resolve(croppedFile)
      },
      'image/jpeg',
      0.95
    )
  })
}

const AdxDropzoneProfileImage = ({ employee, windowCheckEmployeeFile }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorModal, setErrorModal] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedFileName, setSelectedFileName] = useState('profile-photo.jpg')

  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  const fileInputRef = useRef(null)

  const { auth, setAuthData } = useContext(authContext)
  const queryClient = useQueryClient()

  useEffect(() => {
    return () => {
      if (selectedImage && selectedImage.startsWith('blob:')) {
        URL.revokeObjectURL(selectedImage)
      }
    }
  }, [selectedImage])

  const resetCropStates = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setCroppedAreaPixels(null)
  }

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const closeCropModal = () => {
    setCropModalOpen(false)

    if (selectedImage && selectedImage.startsWith('blob:')) {
      URL.revokeObjectURL(selectedImage)
    }

    setSelectedImage(null)
    setSelectedFileName('profile-photo.jpg')
    resetCropStates()
    resetFileInput()
  }

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const validateFile = (file) => {
    if (!file) {
      return 'No file selected.'
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      return 'Only PNG, JPG, JPEG, and WEBP files are allowed.'
    }

    if (file.size > 5000000) {
      return 'File size must be less than 5MB.'
    }

    return null
  }

  const showError = (message, errorObj = null) => {
    setErrorMsg(message)
    setErrorModal(true)

    if (errorObj) {
      Bugsnag.notify(errorObj)
    }
  }

  const handleFileChange = async (event) => {
    try {
      const file = event.target.files?.[0]
      const validationError = validateFile(file)

      if (validationError) {
        resetFileInput()
        showError(validationError)
        return
      }

      if (auth?.data?.tenant_id === undefined) {
        throw new Error('Internal error. Tenant id is undefined.')
      }

      if (selectedImage && selectedImage.startsWith('blob:')) {
        URL.revokeObjectURL(selectedImage)
      }

      const previewUrl = URL.createObjectURL(file)

      setSelectedImage(previewUrl)
      setSelectedFileName(file.name || 'profile-photo.jpg')
      resetCropStates()
      setCropModalOpen(true)
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        e?.data?.message ||
        e?.message ||
        'Oops! Something went wrong while selecting the image.'

      showError(message, e)
    }
  }

  const handleCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels)
  }

  const handleCropAndUpload = async () => {
    setIsLoading(true)

    try {
      if (auth?.data?.tenant_id === undefined) {
        throw new Error('Internal error. Tenant id is undefined.')
      }

      if (!selectedImage || !croppedAreaPixels) {
        throw new Error('No cropped image area found.')
      }

      let croppedFile = await getCroppedImg(selectedImage, croppedAreaPixels, rotation)
      croppedFile = await resizeFile(croppedFile)

      const ext = getFileExtension(selectedFileName) || 'jpg'
      const fileName = generateFileName('photo', ext)

      const uploadFile = new File([croppedFile], fileName, {
        type: croppedFile.type || 'image/jpeg',
      })

      const results = await presignURL({
        bucket: process.env.REACT_APP_AWS_BUCKET,
        key: `tenant_${auth.data.tenant_id}/profile_photo/${uploadFile.name}`,
      })

      const awsRequest = axios.create()
      delete awsRequest.defaults.headers.common.Authorization

      await awsRequest.put(results.data.url, uploadFile, {
        headers: {
          'Content-Type': uploadFile.type,
        },
      })

      await storeProfilePhoto({ profile_image: uploadFile.name })

      await queryClient.invalidateQueries('detailedEmployeeData')
      await queryClient.invalidateQueries(['detailedEmployeeData', employee?.data?.id])

      await setAuthData()

      closeCropModal()
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        e?.data?.message ||
        e?.message ||
        'Oops! Something went wrong!'

      showError(message, e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Box position="relative" mb={1.25}>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius={50}
            bgcolor="white.main"
            sx={{ width: 122, height: 122 }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <Avatar
            sx={{ width: 122, height: 122 }}
            src={employee?.data?.profile_image_url || defaultAvatar}
            imgProps={{
              onError: (e) => {
                e.currentTarget.src = defaultAvatar
              },
            }}
          />
        )}

        {!windowCheckEmployeeFile && (
          <IconButton
            onClick={openFilePicker}
            sx={{
              p: 0,
              width: 31,
              height: 31,
              borderRadius: '50%',
              bgcolor: 'alternate.main',
              position: 'absolute',
              right: 5,
              bottom: 5,
              border: '3px solid',
              borderColor: 'white.main',
              '&:hover': {
                bgcolor: 'green-02.main',
              },
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 15, color: '#fff' }} />
          </IconButton>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Dialog
        open={cropModalOpen}
        onClose={closeCropModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Adjust Profile Picture</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="light-gray-text.main" mb={2}>
            Drag and zoom your photo to choose what appears inside the round profile image.
          </Typography>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 380,
              bgcolor: '#000',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {selectedImage && (
              <Cropper
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={handleCropComplete}
              />
            )}
          </Box>

          <Box mt={3}>
            <Typography variant="body2" fontWeight={500} mb={1}>
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(Array.isArray(value) ? value[0] : value)}
            />
          </Box>

          <Box mt={2}>
            <Typography variant="body2" fontWeight={500} mb={1}>
              Rotate
            </Typography>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(_, value) => setRotation(Array.isArray(value) ? value[0] : value)}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeCropModal} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCropAndUpload} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Photo'}
          </Button>
        </DialogActions>
      </Dialog>

      <AdxInfoModal
        variant="warning"
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        muiIcon="error"
        iconColor="error.main"
        title="Error"
        subtitle={errorMsg}
        footerButtons
        firstBtnLabel="Okay"
        firstBtnHandle={() => {
          setErrorModal(false)
          setErrorMsg('')
        }}
      />
    </>
  )
}

export default AdxDropzoneProfileImage